import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk, RootState } from 'store';
import { appContextSelector } from 'modules/app/appSlice';
import createAllUsersApi from 'api/allUsers';

interface State {
  loading: boolean;
  items: (CustomerUser | FakeUser)[];
  seats?: Seats;
  roles?: Roles;
  inviteQueue: InvitePayload[];
  updateQueue: UpdatePayload[];
}

const initialState: State = {
  loading: false,
  items: [],
  inviteQueue: [],
  updateQueue: [],
};

const slice = createSlice({
  name: 'allUsers',
  initialState,
  reducers: {
    setAllUsers: (state: State, action: PayloadAction<(CustomerUser | FakeUser)[]>): State => {
      state.items = action.payload;
      return state;
    },
    setSeats: (state: State, action: PayloadAction<Seats>): State => {
      state.seats = action.payload;
      return state;
    },
    setRoles: (state: State, action: PayloadAction<Roles>): State => {
      state.roles = action.payload;
      return state;
    },
    setLoading: (state: State, action: PayloadAction<boolean>): State => {
      state.loading = action.payload;
      return state;
    },
    setInviteQueue: (state: State, action: PayloadAction<InvitePayload[]>): State => {
      state.inviteQueue = action.payload;
      return state;
    },
    setUpdateQueue: (state: State, action: PayloadAction<UpdatePayload[]>): State => {
      state.updateQueue = action.payload;
      return state;
    },
  },
});

const { setAllUsers, setLoading, setSeats, setRoles, setInviteQueue, setUpdateQueue } = slice.actions;

export default slice.reducer;

export const getAllUsers = (): AppThunk => async (dispatch, getState): Promise<void> => {
  const { currentAccount } = getState().user;
  const context = appContextSelector(getState());
  const allUsersApi = createAllUsersApi(context);

  if (context === 'connect') {
    return;
  }

  dispatch(setLoading(true));

  try {
    await allUsersApi.getUserSeats(currentAccount?.customerId ?? '');
    const users = await allUsersApi.getAllUsers(currentAccount?.customerId ?? '');

    dispatch(setLoading(false));
    dispatch(setAllUsers(users));
  } catch (err) {
    // eslint-disable-next-line
    console.error(err);
    dispatch(setLoading(false));
  }
};

export const addFakeUsers = (users: FakeUser[]): AppThunk => (dispatch, getState): void => {
  const { items } = getState().allUsers;

  const newUsers = users.filter(user => !items.find(item => item.userId === user.userId));

  dispatch(setAllUsers([...newUsers, ...items]));
};

export const getUserSeats = (): AppThunk => async (dispatch, getState): Promise<void> => {
  const { currentAccount } = getState().user;
  const context = appContextSelector(getState());
  const allUsersApi = createAllUsersApi(context);

  dispatch(setLoading(true));

  try {
    let seats;
    if (context === 'connect') {
      seats = await allUsersApi.getUserSeats(currentAccount?.partnershipId ?? '');
    } else {
      seats = await allUsersApi.getUserSeats(currentAccount?.customerId ?? '');
    }

    dispatch(setLoading(false));
    dispatch(setSeats(seats));
  } catch (err) {
    // eslint-disable-next-line
    console.error(err);
    dispatch(setLoading(false));
  }
};

export const getRoles = (): AppThunk => async (dispatch, getState): Promise<void> => {
  const { currentAccount } = getState().user;
  const context = appContextSelector(getState());
  const allUsersApi = createAllUsersApi(context);

  dispatch(setLoading(true));

  try {
    let roles;
    if (context === 'connect') {
      roles = await allUsersApi.getRoles(currentAccount?.partnershipId ?? '');
    } else {
      roles = await allUsersApi.getRoles(currentAccount?.customerId ?? '');
    }

    dispatch(setLoading(false));
    dispatch(setRoles(roles));
  } catch (err) {
    // eslint-disable-next-line
    console.error(err);
    dispatch(setLoading(false));
  }
};

export const sendInvite = (invitePayload: InvitePayload, onSuccess: CallableFunction, onFailure: CallableFunction): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  const { currentAccount } = getState().user;
  const { roles } = getState().allUsers;
  const context = appContextSelector(getState());
  const allUsersApi = createAllUsersApi(context);

  dispatch(setLoading(true));

  const { email, customerRoleId, employeeCountryCodeIso2, employeeOffset } = invitePayload;

  const payload = {
    profile: {
      name: '',
      preferredName: '',
    },
    email,
    customerRoleId,
    ...(employeeOffset ? { employeeOffset } : null),
    ...(employeeCountryCodeIso2 !== undefined ? { employeeCountryCodeIso2 } : null),
  };

  let data;

  try {
    if (context === 'connect') {
      data = await allUsersApi.createUserInvite(currentAccount?.partnershipId ?? '', payload);
    } else {
      data = await allUsersApi.createUserInvite(currentAccount?.customerId ?? '', payload);
    }

    dispatch(
      addFakeUsers([
        {
          userAccount: { email },
          role: { id: customerRoleId, name: roles?.roles.find(role => role.id === customerRoleId)?.name ?? '' },
          employeeCountryCodeIso2: employeeCountryCodeIso2 ?? '',
          employeeOffset: employeeOffset,
          status: data.status,
          userId: data.userId,
        },
      ]),
    );
    dispatch(getUserSeats());
    dispatch(setLoading(false));
    onSuccess();
  } catch (err) {
    // eslint-disable-next-line
    console.error(err);
    dispatch(setLoading(false));
    onFailure();
  }
};

export const updateUser = (payload: UserPayload, userId: string, onSuccess: CallableFunction, onFailure: CallableFunction): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  const { currentAccount } = getState().user;
  const context = appContextSelector(getState());
  const allUsersApi = createAllUsersApi(context);

  dispatch(setLoading(true));
  try {
    if (context === 'connect') {
      await allUsersApi.updateUser(currentAccount?.partnershipId ?? '', payload, userId);
    } else {
      await allUsersApi.updateUser(currentAccount?.customerId ?? '', payload, userId);
    }
    dispatch(getUserSeats());

    onSuccess();
  } catch (err) {
    // eslint-disable-next-line
    console.error(err);
    dispatch(setLoading(false));
    onFailure();
  }
};

export const deleteUser = (userId: string, onSuccess: CallableFunction, onError: CallableFunction): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  const { currentAccount } = getState().user;
  const { items } = getState().allUsers;
  const context = appContextSelector(getState());
  const allUsersApi = createAllUsersApi(context);

  dispatch(setLoading(true));
  try {
    if (context === 'connect') {
      await allUsersApi.deleteUser(currentAccount?.partnershipId ?? '', userId);
    } else {
      await allUsersApi.deleteUser(currentAccount?.customerId ?? '', userId);
    }

    dispatch(setAllUsers(items.filter(item => item.userId !== userId)));
    dispatch(getUserSeats());

    dispatch(setLoading(false));
    onSuccess();
  } catch (err) {
    // eslint-disable-next-line
    console.error(err);
    dispatch(setLoading(false));
    onError();
  }
};

export const addNewInviteToQueue = (payload: InvitePayload): AppThunk => (dispatch, getState): void => {
  const { inviteQueue } = getState().allUsers;
  dispatch(setInviteQueue([...(inviteQueue ?? []), payload]));
};

export const clearInviteQueue = (): AppThunk => (dispatch): void => {
  dispatch(setInviteQueue([]));
};

export const addNewUpdateToQueue = (payload: UpdatePayload): AppThunk => (dispatch, getState): void => {
  const { updateQueue } = getState().allUsers;
  dispatch(setUpdateQueue([...(updateQueue ?? []), payload]));
};

export const clearUpdateQueue = (): AppThunk => (dispatch): void => {
  dispatch(setUpdateQueue([]));
};

export const sendInviteBatch = (onSuccess: CallableFunction, onFailure: CallableFunction): AppThunk => async (dispatch, getState): Promise<void> => {
  const { inviteQueue } = getState().allUsers;

  inviteQueue.forEach(invite => {
    dispatch(sendInvite(invite, onSuccess, onFailure));
  });

  dispatch(clearInviteQueue());
};

export const sendUpdateBatch = (onSuccess: CallableFunction, onFailure: CallableFunction): AppThunk => async (dispatch, getState): Promise<void> => {
  const { updateQueue } = getState().allUsers;

  updateQueue.forEach(update => {
    dispatch(updateUser(update, update.userId, onSuccess, onFailure));
  });

  dispatch(clearUpdateQueue());
};

export const allRolesSelector = (state: RootState): Role[] | undefined => state.allUsers.roles?.roles;

export const availableSeatsSelector = (state: RootState): number | undefined => state.allUsers.seats?.seats.available;

export const offsetEmployeesSelector = (state: RootState): number =>
  state.allUsers.items.reduce((acc, curr) => acc + (curr?.employeeOffset ? 1 : 0), 0) ?? 0;

export const availableIncludedSeatsSelector = (state: RootState): number => state.allUsers.seats?.includedSeats.available ?? 0;
export const totalIncludedSeatsSelector = (state: RootState): number => state.allUsers.seats?.includedSeats.total ?? 0;
